import { __ } from '@99designs/i18n';
import { ListingWorkItemsQuery } from '../queries/ListingWorkItems.generated';
import { get99dDomain } from '../utils/domains';

export function convertDateToLongDate(createdDate: string) {
    return new Date(createdDate).toLocaleDateString(undefined, {
        dateStyle: 'long',
    });
}

export type WorkItem = ListingWorkItemsQuery['yourWorkItems']['items'][0];

export type PageStage = 'Discover' | 'Design' | 'My Account';
export type PageSection = 'Design Services' | 'My Account';

//TODO: Restrict action to only valid Design Services actions
interface AnalyticsEventData {
    label: string;
    pageStage: PageStage;
    pageName: string;
    eventDetail?: unknown;
    category?: string;
    pageSection?: string;
    [key: string]: unknown;
}
export interface Project {
    id: string;
    title: string;
    section: 'current' | 'past';
    previewUrl?: string;
    description: string;
    createdDate: string;
    createdDateTimestamp: string;
    categoryTitle: string;
    projectUrl: string;
    designer?: {
        displayName: string;
        avatarUrl: string;
        profileUrl: string;
        analytics: AnalyticsEventData;
    };
    analytics: AnalyticsEventData;
    jobId?: string;
}

function convertToCapitalizedWords(category: string) {
    return category
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function getAvatarUrl(designerId?: number | string | null, size = 64) {
    if (!designerId) {
        return '';
    }

    const baseUrl =
        process.env.VITE_DESIGN_SERVICES_DASHBOARD_AVATAR_BASE_PATH ||
        'https://avatars.99static.com/avatars';

    return `${baseUrl}/${designerId}/${size}`;
}

export function workItemToProject(w: WorkItem): Project[] {
    switch (w.__typename) {
        case 'StandardWorkItem':
            // eslint-disable-next-line no-case-declarations
            const section: Project['section'] = w.status === 'IN_PROGRESS' ? 'current' : 'past';
            return [
                {
                    title: w.title,
                    section: section,
                    previewUrl: w.icon.url,
                    description: __(
                        'Head to 99designs to work on your project and collaborate with your designer.'
                    ),
                    createdDate: convertDateToLongDate(w.stage.date),
                    createdDateTimestamp: w.stage.date,
                    id: w.id,
                    designer: {
                        displayName: w.collaborators[0].displayName,
                        avatarUrl: getAvatarUrl(w.collaborators[0].id),
                        profileUrl: `https://${get99dDomain()}/profiles/${w.collaborators[0].id}`,
                        analytics: {
                            label: `My design services:${section}`,
                            pageName: 'Design Services:Dashboard',
                            pageStage: 'My Account',
                            category: 'Design Services',
                            eventDetail: `/graphic-design/dashboard;https://${get99dDomain()}/profiles/${
                                w.collaborators[0].id
                            };myDesignServices;99designs-${w.collaborators[0].displayName}`,
                            pageSection: 'Design Services',
                        },
                    },
                    categoryTitle: convertToCapitalizedWords(w.category ?? ''),
                    projectUrl: `https://${get99dDomain()}${w.linkUrl}`,
                    analytics: {
                        label: `My design services:${section}`,
                        pageName: 'Design Services:Dashboard',
                        pageStage: 'My Account',
                        category: 'Design Services',
                        eventDetail: `/graphic-design/dashboard;${w.linkUrl};myDesignServices;99designs-open-project`,
                        pageSection: 'Design Services',
                    },
                },
            ];
        case 'OrderWorkItem':
            return w.items.flatMap((subItem): Project[] => {
                switch (subItem.__typename) {
                    case 'OrderWorkSubItemProject':
                        if (subItem.state === 'READY') {
                            const url =
                                subItem.job != null
                                    ? `https://${get99dDomain()}/projects/job/${
                                          subItem.job.id
                                      }/designers`
                                    : `https://${get99dDomain()}/briefs/order-item/${subItem.id}`;
                            return [
                                {
                                    title: subItem.brief?.title ?? '',
                                    section: 'current',
                                    description: __(
                                        'Head to 99designs to work on your project and collaborate with your designer.'
                                    ),
                                    id: subItem.id,
                                    categoryTitle: convertToCapitalizedWords(
                                        subItem.brief?.category?.title ?? ''
                                    ),
                                    createdDate: convertDateToLongDate(w.createdAt),
                                    createdDateTimestamp: w.createdAt,
                                    projectUrl: url,
                                    analytics: {
                                        label: `My design services:current`,
                                        pageName: 'Design Services:Dashboard',
                                        pageStage: 'My Account',
                                        category: 'Design Services',
                                        eventDetail: `/graphic-design/dashboard;${url};myDesignServices;99designs-open-project`,
                                        pageSection: 'Design Services',
                                    },
                                    jobId: subItem.job?.id, // Only expose jobId if in the job matching stage
                                },
                            ];
                        }
                        if (subItem.project != null) {
                            // eslint-disable-next-line no-case-declarations
                            const section: Project['section'] =
                                subItem.state !== 'COMPLETED' && subItem.state !== 'REFUNDED'
                                    ? 'current'
                                    : 'past';
                            return [
                                {
                                    title: subItem.project.title,
                                    section:
                                        subItem.state !== 'COMPLETED' &&
                                        subItem.state !== 'REFUNDED'
                                            ? 'current'
                                            : 'past',
                                    description: __(
                                        'Head to 99designs to work on your project and collaborate with your designer.'
                                    ),
                                    createdDate: convertDateToLongDate(w.createdAt),
                                    createdDateTimestamp: w.createdAt,
                                    id: subItem.id,
                                    categoryTitle: convertToCapitalizedWords(
                                        subItem.project.category?.title ?? ''
                                    ),
                                    projectUrl: `https://${get99dDomain()}/projects/${
                                        subItem.project.id
                                    }`,
                                    designer: {
                                        profileUrl: `https://${get99dDomain()}/profiles/${
                                            subItem.project.designer?.id
                                        }`,
                                        displayName: subItem.project.designer?.displayName ?? '',
                                        avatarUrl: getAvatarUrl(subItem.project.designer?.id),
                                        analytics: {
                                            label: `My design services:${section}`,
                                            pageName: 'Design Services:Dashboard',
                                            pageStage: 'My Account',
                                            category: 'Design Services',
                                            eventDetail: `/graphic-design/dashboard;https://${get99dDomain()}/profiles/${
                                                subItem.project.designer?.id
                                            };myDesignServices; 99designs-${
                                                subItem.project.designer?.displayName
                                            }`,
                                            pageSection: 'Design Services',
                                        },
                                    },
                                    analytics: {
                                        label: `My design services:${section}`,
                                        pageName: 'Design Services:Dashboard',
                                        pageStage: 'My Account',
                                        category: 'Design Services',
                                        eventDetail: `/graphic-design/dashboard;https://${get99dDomain()}/projects/${
                                            subItem.project.id
                                        };myDesignServices;99designs-open-project`,
                                        pageSection: 'Design Services',
                                    },
                                },
                            ];
                        }
                        return [];
                    default:
                        return [];
                }
            });
        default:
            return [];
    }
}
